import { SiteImageFragment } from '@graphql/fragments/__generated__/SiteImage.generated'
import { useSiteQuery } from '@graphql/queries/__generated__/site.generated'
import { Theme } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import makeStyles from '@mui/styles/makeStyles'
import React, { useMemo, useState } from 'react'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { maybe } from '../../../utils/misc'
import { CLIENT_URL } from '../../../../constants'
import { default as MaterialLink } from '@mui/material/Link'
import { Link } from 'react-router-dom'

const useStyles = makeStyles((theme: Theme) => ({
  description: {
    [theme.breakpoints.down('sm')]: {
      '& *': {
        color: '#000 !important',
        textAlign: 'center !important',
      },
    },
  },
}))
const SliderSkeleton = () => {
  return (
    <>
      <div className='pb-full sm:pb-half bg-blue-50' />
      <div className='text-center mt-30'>
        <h3 className='flex flex-col items-center font-bold text-5xl mb-20 font-futura'>
          <Skeleton width={200} />
        </h3>
        <div className='flex flex-col items-center py-20 px-20 md:px-40 bg-orange text-lg md:text-2xl leading-normal'>
          <Skeleton width={'95%'} />
          <Skeleton width={'90%'} />
          <Skeleton width={'60%'} />
        </div>
      </div>
    </>
  )
}

const ImageLinkContainer: React.FC<{ link: string }> = ({ link, children }) => {
  const isExternal = !link.includes(CLIENT_URL)
  const url = isExternal ? link : new URL(link).pathname + new URL(link).search
  if (isExternal) {
    return (
      <MaterialLink className='block no-underline' href={url} target='_blank'>
        {children}
      </MaterialLink>
    )
  }
  return (
    <Link className='block no-underline' to={url}>
      {children}
    </Link>
  )
}

const SiteImagesSlider: React.FC = () => {
  const classes = useStyles()
  const [activeSlide, setActiveSlide] = useState<SiteImageFragment | null>(null)
  const siteQueryResult = useSiteQuery({
    fetchPolicy: 'cache-first',
    onCompleted: data => {
      if (data.siteSettings.site.images.length > 0) {
        setActiveSlide(data?.siteSettings.site.images[0])
      }
    },
  })
  const images: SiteImageFragment[] = useMemo(
    () => maybe(() => siteQueryResult.data.siteSettings.site.images, []),
    [siteQueryResult.data]
  )
  const swiperOptions: SwiperOptions = useMemo(
    () => ({
      spaceBetween: 0,
      slidesPerView: 1,
      lazy: true,
      autoHeight: true,
      preloadImages: false,
      pagination: {
        clickable: true,
        el: `#swiper-pagination-home`,
      },
      watchSlidesProgress: true,
      loop: true,
      allowSlideNext: images.length > 1,
      allowSlidePrev: images.length > 1,
      navigation: {
        prevEl: `#swiper-button-prev-home`,
        nextEl: `#swiper-button-next-home`,
      },
      autoplay: {
        disableOnInteraction: false,
        delay: 60 * 1000,
        waitForTransition: true,
      },
      onActiveIndexChange: ({ realIndex }) => {
        setActiveSlide(images[realIndex] || images[0])
      },
    }),
    [images]
  )

  if (!images.length && !siteQueryResult.loading) return null
  return (
    <>
      <div className='relative mb-20'>
        {siteQueryResult.loading && images.length === 0 && <SliderSkeleton />}
        {images.length > 0 && (
          <>
            <Swiper {...swiperOptions}>
              {images.map(el => {
                return (
                  <SwiperSlide key={el.id}>
                    <ImageLinkContainer link={el.link}>
                      <div
                        className='relative items-center bg-center bg-no-repeat bg-cover bg-blue-50 flex sm:hidden'
                        style={{ paddingBottom: '66.5%' }}
                      >
                        <div className='absolute inset-0 h-full'>
                          <picture>
                            <source srcSet={el.thumbnailUrl} media='(min-width: 601px)' />
                            <source srcSet={el.mobileThumbnailUrl || el.thumbnailUrl} media='(max-width: 600px)' />
                            <img
                              className='w-full h-full object-cover object-center'
                              src={el.thumbnailUrl}
                              alt={el.alt || el.title || el.link}
                            />
                          </picture>
                        </div>
                      </div>
                      <div
                        className='relative items-center bg-center bg-no-repeat bg-cover bg-blue-50 hidden sm:flex'
                        style={{ paddingBottom: '50%' }}
                      >
                        <div className='absolute inset-0 h-full'>
                          <picture>
                            <source srcSet={el.thumbnailUrl} media='(min-width: 601px)' />
                            <source srcSet={el.mobileThumbnailUrl || el.thumbnailUrl} media='(max-width: 600px)' />
                            <img
                              className='w-full h-full object-cover object-center'
                              src={el.thumbnailUrl}
                              alt={el.alt || el.title || el.link}
                            />
                          </picture>
                        </div>
                        <div className='z-10 px-15 left-0 right-0 absolute' style={{ bottom: '8%' }}>
                          <div className={classes.description} dangerouslySetInnerHTML={{ __html: el.description }} />
                        </div>
                      </div>
                    </ImageLinkContainer>
                  </SwiperSlide>
                )
              })}
            </Swiper>
            <div
              id={`swiper-pagination-home`}
              className='swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full sm:bottom-40 md-swiper-pagination--white swiper-pagination--large'
            />
            {images.length > 1 && (
              <>
                <div
                  id={`swiper-button-prev-home`}
                  className='swiper-button-prev hidden md:flex w-50 h-100 transform -translate-y-1/2'
                />
                <div
                  id={`swiper-button-next-home`}
                  className='swiper-button-next hidden md:flex w-50 h-100 transform -translate-y-1/2'
                />
              </>
            )}
          </>
        )}
      </div>
      {!!activeSlide && (!!activeSlide.title || !!activeSlide.annotation) && (
        <div className='text-center mt-30 hidden sm:block'>
          <h3 className='font-bold text-5xl mb-20 font-futura'>{activeSlide.title}</h3>
          <div className='py-20 px-20 md:px-40 bg-orange text-lg md:text-2xl leading-normal'>
            {activeSlide.annotation}
          </div>
        </div>
      )}
    </>
  )
}

export default SiteImagesSlider
